import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import {
  getWantedAds, createWantedAd, updateWantedAd, deleteWantedAd,
  getMyWantedAds, getCategoriesFlat, createMessageThread, fulfillWantedAd,
} from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search, PlusCircle, MapPin, Calendar, Ruler, Package, Trash2, AlertCircle,
  ClipboardList, ChevronLeft, ChevronRight, Info, MessageSquare, Send,
  Pencil, CheckCircle, Euro,
} from 'lucide-react';
import { toast } from 'sonner';
import { PostWantedAdDialog } from '@/components/wanted/PostWantedAdDialog';
import { OfferWizard } from '@/components/offers/OfferWizard';
import { OfferComparisonDialog } from '@/components/offers/OfferComparisonDialog';

const UNITS = ['kg', 'sqm', 'rolls', 'pcs', 'lm', 'pallet'];

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const daysLeft = (validUntil) => Math.ceil((new Date(validUntil) - new Date()) / 86400000);

// ─── Post Wanted Ad: use shared PostWantedAdDialog from /components/wanted ───


// ─── Edit Wanted Ad Modal ────────────────────────────────────────────────────
function EditWantedAdModal({ open, onClose, ad, onUpdated }) {
  const [form, setForm] = useState({ material_type_description: '', width_min_mm: '', width_max_mm: '', min_quantity: '', quantity_unit: 'kg', price_min_eur: '', price_max_eur: '', valid_until: '' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (ad && open) {
      setForm({
        material_type_description: ad.material_type_description || '',
        width_min_mm: ad.width_min_mm != null ? String(ad.width_min_mm) : '',
        width_max_mm: ad.width_max_mm != null ? String(ad.width_max_mm) : '',
        min_quantity: ad.min_quantity != null ? String(ad.min_quantity) : '',
        quantity_unit: ad.quantity_unit || 'kg',
        price_min_eur: ad.price_min_eur != null ? String(ad.price_min_eur) : '',
        price_max_eur: ad.price_max_eur != null ? String(ad.price_max_eur) : '',
        valid_until: ad.valid_until || '',
      });
    }
  }, [ad, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.material_type_description || !form.min_quantity || !form.valid_until) {
      toast.error('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    setLoading(true);
    try {
      await updateWantedAd(ad.wanted_id, {
        material_type_description: form.material_type_description,
        width_min_mm: form.width_min_mm ? parseFloat(form.width_min_mm) : null,
        width_max_mm: form.width_max_mm ? parseFloat(form.width_max_mm) : null,
        min_quantity: parseFloat(form.min_quantity),
        quantity_unit: form.quantity_unit,
        price_min_eur: form.price_min_eur ? parseFloat(form.price_min_eur) : null,
        price_max_eur: form.price_max_eur ? parseFloat(form.price_max_eur) : null,
        valid_until: form.valid_until,
      });
      toast.success('Gesuch erfolgreich aktualisiert.');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Aktualisieren.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="edit-wanted-ad-modal">
        <DialogHeader>
          <DialogTitle>Gesuch bearbeiten</DialogTitle>
          <DialogDescription>Aktualisieren Sie die Details Ihres Gesuchs.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label>Materialbeschreibung <span className="text-red-500">*</span></Label>
            <Input value={form.material_type_description} onChange={(e) => set('material_type_description', e.target.value)} data-testid="edit-wanted-description" />
          </div>
          <div className="space-y-1.5">
            <Label>Breitenbereich (mm) <span className="text-muted-foreground text-xs ml-1">optional</span></Label>
            <div className="flex gap-3">
              <Input type="number" placeholder="Min. mm" value={form.width_min_mm} onChange={(e) => set('width_min_mm', e.target.value)} className="flex-1" data-testid="edit-wanted-width-min" />
              <span className="self-center text-muted-foreground">–</span>
              <Input type="number" placeholder="Max. mm" value={form.width_max_mm} onChange={(e) => set('width_max_mm', e.target.value)} className="flex-1" data-testid="edit-wanted-width-max" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Mindestmenge <span className="text-red-500">*</span></Label>
            <div className="flex gap-3">
              <Input type="number" placeholder="Menge" value={form.min_quantity} onChange={(e) => set('min_quantity', e.target.value)} className="flex-1" data-testid="edit-wanted-quantity" />
              <Select value={form.quantity_unit} onValueChange={(v) => set('quantity_unit', v)}>
                <SelectTrigger className="w-28" data-testid="edit-wanted-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Preisvorstellung (EUR / Einheit) <span className="text-muted-foreground text-xs ml-1">optional</span></Label>
            <div className="flex gap-3">
              <Input type="number" step="0.01" min="0" placeholder="Min. €" value={form.price_min_eur} onChange={(e) => set('price_min_eur', e.target.value)} className="flex-1" data-testid="edit-wanted-price-min" />
              <span className="self-center text-muted-foreground">–</span>
              <Input type="number" step="0.01" min="0" placeholder="Max. €" value={form.price_max_eur} onChange={(e) => set('price_max_eur', e.target.value)} className="flex-1" data-testid="edit-wanted-price-max" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Gültig bis <span className="text-red-500">*</span></Label>
            <Input type="date" value={form.valid_until} onChange={(e) => set('valid_until', e.target.value)} min={new Date().toISOString().slice(0, 10)} data-testid="edit-wanted-valid-until" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Abbrechen</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={loading} data-testid="edit-wanted-submit">
              {loading ? 'Wird gespeichert…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Offer Modal ─────────────────────────────────────────────────────────────
function OfferModal({ open, onClose, ad, onSent }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setLoading(true);
    try {
      const res = await createMessageThread(ad.wanted_id, content.trim());
      toast.success('Angebot gesendet! Verfolgen Sie die Konversation unter "Nachrichten".');
      setContent('');
      onSent(res.data.thread_id);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Senden des Angebots.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { setContent(''); onClose(); }}>
      <DialogContent className="max-w-md" data-testid="offer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-500" />
            Angebot senden
          </DialogTitle>
          <DialogDescription>
            Schreiben Sie dem Inserenten eine Nachricht. Name und Firma werden nach dem ersten Kontakt sichtbar.
          </DialogDescription>
        </DialogHeader>
        {ad && (
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-sm">
            <p className="text-xs text-muted-foreground font-medium mb-1">Gesuch:</p>
            <p className="font-semibold">{ad.material_type_description}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {ad.category_name} · {ad.min_quantity} {ad.quantity_unit}
              {ad.poster_postal_code || ad.poster_country
                ? ` · ${[ad.poster_postal_code, ad.poster_country].filter(Boolean).join(', ')}`
                : ''}
            </p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Ihre Nachricht <span className="text-red-500">*</span></Label>
            <Textarea
              placeholder="Beschreiben Sie Ihr Angebot, verfügbare Menge, Qualität, Konditionen…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[120px] resize-none"
              data-testid="offer-message-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Abbrechen</Button>
            <Button type="submit" className="bg-blue-500 hover:bg-blue-600 gap-2" disabled={!content.trim() || loading} data-testid="offer-send-btn">
              <Send className="h-4 w-4" />
              {loading ? 'Wird gesendet…' : 'Angebot senden'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Fulfill Modal ────────────────────────────────────────────────────────────
function FulfillModal({ open, onClose, ad, onFulfilled }) {
  const [amount, setAmount] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleClose = () => { setAmount(''); setDiscountCode(''); setResult(null); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) {
      toast.error('Bitte einen gültigen Betrag eingeben.');
      return;
    }
    setLoading(true);
    try {
      const res = await fulfillWantedAd(ad.wanted_id, {
        transaction_amount_eur: parsed,
        discount_code: discountCode.trim() || undefined,
      });
      setResult(res.data);
      toast.success('Gesuch als erfüllt markiert!');
      onFulfilled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Markieren als erfüllt.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="fulfill-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Gesuch als erfüllt markieren
          </DialogTitle>
          <DialogDescription>
            Transaktionsbetrag eingeben — die Käufer-Provision wird automatisch berechnet.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2 text-sm">
              <p className="font-semibold text-green-800 dark:text-green-300 mb-2">Transaktion erfasst</p>
              <div className="flex justify-between text-muted-foreground">
                <span>Transaktionsbetrag</span>
                <span className="font-medium text-foreground">{result.breakdown?.transaction_amount_eur?.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Provisionsrate</span>
                <span className="font-medium text-foreground">{result.breakdown?.final_rate?.toFixed(2)} %</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-1">
                <span className="font-semibold">Provision (Käufer)</span>
                <span className="font-bold text-orange-600">{result.commission_eur?.toFixed(2)} €</span>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose} data-testid="fulfill-close-btn">Schließen</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {ad && (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-sm">
                <p className="text-xs text-muted-foreground font-medium mb-1">Gesuch:</p>
                <p className="font-semibold">{ad.material_type_description}</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Transaktionsbetrag (€) <span className="text-red-500">*</span></Label>
              <Input
                type="number" step="0.01" min="0.01" placeholder="z. B. 1500.00"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="fulfill-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rabattcode <span className="text-muted-foreground text-xs ml-1">optional</span></Label>
              <Input
                placeholder="z. B. SUMMER10"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                data-testid="fulfill-discount-code"
              />
            </div>
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-300">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Die Provision trägt der <strong>Käufer</strong> und wird automatisch im Provisionssystem erfasst.</span>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>Abbrechen</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 gap-2" disabled={!amount || loading} data-testid="fulfill-confirm-btn">
                <CheckCircle className="h-4 w-4" />
                {loading ? 'Wird verarbeitet…' : 'Als erfüllt markieren'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Wanted Ad Card ───────────────────────────────────────────────────────────
function WantedAdCard({ ad, onDelete, onOffer, onEdit, onFulfill, onViewOffers, isOwner, canOffer, highlight }) {
  const days = daysLeft(ad.valid_until);
  const isExpiringSoon = days <= 7 && days > 0;
  const isFulfilled = ad.fulfilled;

  return (
    <Card
      id={`wanted-card-${ad.wanted_id}`}
      className={`hover:shadow-md transition-shadow ${isFulfilled ? 'opacity-60' : ''} ${highlight ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
      data-testid={`wanted-ad-${ad.wanted_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                {ad.category_name}
              </Badge>
              {isFulfilled && (
                <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  Erfüllt
                </Badge>
              )}
              {isExpiringSoon && !isFulfilled && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                  Läuft bald ab
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-base leading-tight">{ad.material_type_description}</h3>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-muted-foreground mt-3">
          <div className="flex items-center gap-1.5">
            <Package className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            <span>{ad.min_quantity} {ad.quantity_unit} Mindestmenge</span>
          </div>
          {(ad.price_min_eur != null || ad.price_max_eur != null) && (
            <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-medium">
              <Euro className="h-3.5 w-3.5 shrink-0" />
              <span>
                {ad.price_min_eur != null && ad.price_max_eur != null
                  ? `${Number(ad.price_min_eur).toFixed(2)} – ${Number(ad.price_max_eur).toFixed(2)} €`
                  : ad.price_min_eur != null
                  ? `ab ${Number(ad.price_min_eur).toFixed(2)} €`
                  : `bis ${Number(ad.price_max_eur).toFixed(2)} €`}
              </span>
            </div>
          )}
          {(ad.width_min_mm != null || ad.width_max_mm != null) && (
            <div className="flex items-center gap-1.5">
              <Ruler className="h-3.5 w-3.5 text-purple-500 shrink-0" />
              <span>
                {ad.width_min_mm != null ? ad.width_min_mm : '?'} –{' '}
                {ad.width_max_mm != null ? ad.width_max_mm : '?'} mm
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-green-500 shrink-0" />
            <span>{[ad.poster_postal_code, ad.poster_country].filter(Boolean).join(', ') || 'Standort unbekannt'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-orange-500 shrink-0" />
            <span className={days <= 3 && !isFulfilled ? 'text-red-500 font-medium' : ''}>
              bis {formatDate(ad.valid_until)}
              {days > 0 && !isFulfilled ? ` (${days}d)` : isFulfilled ? '' : ' (abgelaufen)'}
            </span>
          </div>
        </div>

        {/* Action row */}
        <div className="mt-3 pt-3 border-t flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs font-medium">
            <Info className="h-3 w-3" />
            Käufer trägt Provision
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {canOffer && !isFulfilled && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5 border-blue-400 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => onOffer(ad)}
                data-testid={`offer-btn-${ad.wanted_id}`}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Anbieten
              </Button>
            )}
            {isOwner && onViewOffers && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1 border-orange-400 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                onClick={() => onViewOffers(ad)}
                data-testid={`view-offers-${ad.wanted_id}`}
              >
                <ClipboardList className="h-3.5 w-3.5" />
                Angebote
              </Button>
            )}
            {isOwner && onEdit && !isFulfilled && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-slate-100"
                onClick={() => onEdit(ad)}
                data-testid={`edit-wanted-ad-${ad.wanted_id}`}
                title="Bearbeiten"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {isOwner && onFulfill && !isFulfilled && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                onClick={() => onFulfill(ad)}
                data-testid={`fulfill-btn-${ad.wanted_id}`}
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Erfüllt
              </Button>
            )}
            {isOwner && onDelete && !isFulfilled && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => onDelete(ad.wanted_id)}
                data-testid={`delete-wanted-ad-${ad.wanted_id}`}
                title="Löschen"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function WantedMaterialsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterText, setFilterText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [tab, setTab] = useState(searchParams.get('tab') === 'my' ? 'my' : 'all');
  const LIMIT = 12;

  // Modal states
  const [offerAd, setOfferAd] = useState(null);
  const [editAd, setEditAd] = useState(null);
  const [fulfillAd, setFulfillAd] = useState(null);
  const [comparisonAd, setComparisonAd] = useState(null);
  const [highlightId, setHighlightId] = useState(searchParams.get('highlight') || null);

  const canPost = !!user;

  const loadCategories = useCallback(async () => {
    try {
      const res = await getCategoriesFlat();
      setCategories(res.data?.categories || res.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadAds = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filterCategory) params.category_node_id = filterCategory;
      const res = await getWantedAds(params);
      setAds(res.data.ads);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user, page, filterCategory]);

  const loadMyAds = useCallback(async () => {
    if (!user || !canPost) return;
    try {
      const res = await getMyWantedAds();
      setMyAds(res.data.ads);
    } catch (e) { console.error(e); }
  }, [user, canPost]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadAds(); }, [loadAds]);
  useEffect(() => { if (tab === 'my') loadMyAds(); }, [tab, loadMyAds]);

  // Deep-link from homepage: /wanted?highlight={wanted_id} — scroll to + highlight the card
  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.getElementById(`wanted-card-${highlightId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const next = new URLSearchParams(searchParams);
      next.delete('highlight');
      setSearchParams(next, { replace: true });
      const t = setTimeout(() => setHighlightId(null), 3000);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, loading, ads]);

  // Deep-link from emails / notifications: /wanted?tab=my&open_offers={wanted_id}
  useEffect(() => {
    const wantedId = searchParams.get('open_offers');
    if (!wantedId || !user) return;
    if (tab !== 'my') setTab('my');
    (async () => {
      try {
        const res = await getMyWantedAds();
        const ads = res.data?.ads || [];
        setMyAds(ads);
        const target = ads.find((a) => a.wanted_id === wantedId);
        if (target) setComparisonAd(target);
        // Clear param so it does not re-trigger on close
        const next = new URLSearchParams(searchParams);
        next.delete('open_offers');
        setSearchParams(next, { replace: true });
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams.get('open_offers')]);

  const handleDelete = async (id) => {
    if (!window.confirm('Gesuch wirklich löschen?')) return;
    try {
      await deleteWantedAd(id);
      toast.success('Gesuch gelöscht.');
      loadMyAds();
      loadAds();
    } catch { toast.error('Fehler beim Löschen.'); }
  };

  const handleOfferSent = (threadId) => {
    navigate(`/messages?thread=${threadId}`);
  };

  const handleFulfilled = () => {
    loadMyAds();
    loadAds();
  };

  const filteredAds = filterText
    ? ads.filter((a) =>
        a.material_type_description?.toLowerCase().includes(filterText.toLowerCase()) ||
        a.category_name?.toLowerCase().includes(filterText.toLowerCase())
      )
    : ads;

  const totalPages = Math.ceil(total / LIMIT);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-orange-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Anmeldung erforderlich</h2>
          <p className="text-muted-foreground mb-6">Bitte melden Sie sich an, um Gesuche anzusehen.</p>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => navigate('/login')}>Anmelden</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="wanted-materials-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Page Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-manrope text-3xl font-bold flex items-center gap-2" data-testid="wanted-page-title">
              <ClipboardList className="h-7 w-7 text-orange-500" />
              Gesuchte Materialien
            </h1>
            <p className="text-muted-foreground mt-1">
              Plattformweite Gesuche nach Surplus-Materialien. Anonymisiert — nur Land und PLZ sichtbar.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button className="bg-orange-500 hover:bg-orange-600 gap-2" onClick={() => setModalOpen(true)} data-testid="post-wanted-ad-btn">
              <PlusCircle className="h-4 w-4" />
              Gesuch einstellen
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="all" data-testid="wanted-tab-all">
              Alle Gesuche
              {total > 0 && <Badge variant="secondary" className="ml-2 text-xs">{total}</Badge>}
            </TabsTrigger>
            {canPost && (
              <TabsTrigger value="my" data-testid="wanted-tab-my">
                Meine Gesuche
                {myAds.length > 0 && <Badge variant="secondary" className="ml-2 text-xs">{myAds.length}</Badge>}
              </TabsTrigger>
            )}
          </TabsList>

          {/* All Ads */}
          <TabsContent value="all" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Gesuche durchsuchen…" value={filterText} onChange={(e) => setFilterText(e.target.value)} className="pl-9" data-testid="wanted-search-input" />
              </div>
              <Select value={filterCategory || 'all'} onValueChange={(v) => { setFilterCategory(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-56" data-testid="wanted-category-filter"><SelectValue placeholder="Alle Kategorien" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {categories.map((c) => <SelectItem key={c.node_id} value={c.node_id}>{c.node_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-52" />)}
              </div>
            ) : filteredAds.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground" data-testid="wanted-empty-state">
                <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">Keine aktiven Gesuche gefunden</p>
                <p className="text-sm mt-1">{canPost ? 'Stellen Sie das erste Gesuch ein!' : 'Noch keine Gesuche vorhanden.'}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredAds.map((ad) => (
                    <WantedAdCard
                      key={ad.wanted_id}
                      ad={ad}
                      isOwner={ad.is_own === true}
                      canOffer={!ad.is_own}
                      onOffer={setOfferAd}
                      highlight={ad.wanted_id === highlightId}
                    />
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-4">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="wanted-prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">Seite {page} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="wanted-next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* My Ads */}
          {canPost && (
            <TabsContent value="my" className="space-y-4">
              {myAds.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground" data-testid="my-wanted-empty">
                  <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Sie haben noch keine Gesuche eingestellt</p>
                  <Button className="mt-4 bg-orange-500 hover:bg-orange-600 gap-2" onClick={() => setModalOpen(true)} data-testid="post-first-wanted-ad">
                    <PlusCircle className="h-4 w-4" />
                    Erstes Gesuch einstellen
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myAds.map((ad) => (
                    <WantedAdCard
                      key={ad.wanted_id}
                      ad={ad}
                      isOwner
                      onDelete={handleDelete}
                      onEdit={setEditAd}
                      onFulfill={setFulfillAd}
                      onViewOffers={setComparisonAd}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Modals */}
      <PostWantedAdDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => { loadAds(); loadMyAds(); }}
        initialCategories={categories}
      />
      <OfferWizard
        open={!!offerAd}
        ad={offerAd}
        onClose={() => setOfferAd(null)}
        onSubmitted={() => { setOfferAd(null); loadAds(); }}
      />
      <OfferComparisonDialog
        open={!!comparisonAd}
        ad={comparisonAd}
        onClose={() => setComparisonAd(null)}
        onChanged={() => { loadAds(); loadMyAds(); }}
      />
      <EditWantedAdModal
        open={!!editAd}
        ad={editAd}
        onClose={() => setEditAd(null)}
        onUpdated={() => { loadMyAds(); loadAds(); }}
      />
      <FulfillModal
        open={!!fulfillAd}
        ad={fulfillAd}
        onClose={() => setFulfillAd(null)}
        onFulfilled={handleFulfilled}
      />
    </div>
  );
}
